/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { ReactElement, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useParams } from 'react-router-dom';
// import { v4 as uuidv4 } from 'uuid';

import { Flex, Box, Typography, Loading } from 'components';
import { request } from 'app/utils';

import { testCpf, splitName, cpfMask, cepMask, dateMask, cellPhoneMask } from './utils';
import { FormInput, FormLabel, FormErrorMessage, FormSubmitButton, Hr } from './styles';
import styled from 'styled-components';
import { useSchool } from 'features/school';

interface SignupFormProps {
  optin: boolean;
  setOptin: Dispatch<SetStateAction<boolean>>;
  showTerm: boolean;
  setShowTerm: Dispatch<SetStateAction<boolean>>;
}

const SignupForm = ({ optin, setOptin, showTerm, setShowTerm }: SignupFormProps): ReactElement => {
  const [loading, setLoading] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { apiGatewayUrl, tenantUuid, schoolUuid } = useSchool();
  const { affiliateId } = useParams();

  // const productUuid = '846961db-dd65-4862-841a-baf33a715e1c'; // Ancord
  const productUuid = '3cefee52-57de-407e-8bde-d46d2c9fff22'; // Mentoria

  const formik = useFormik({
    initialValues: {
      name: '',
      taxDocument: '',
      email: '',
      birthDate: '',
      cellPhone: '',
      address: '',
      addressNumber: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      zipcode: '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Você precisa informar seu nome')
        .matches(/(\w.+\s).+/, 'Informe seu nome completo'),

      taxDocument: Yup.string()
        .required('Você precisa informar seu CPF')
        .test('taxDocument', 'CPF Inválido', (value) => testCpf(`${value}`)),

      email: Yup.string().required('Você precisa informar seu e-mail').email('O e-mail informado está inválido'),

      birthDate: Yup.string()
        .required('Você precisa informar sua data de nascimento')
        .matches(/^\d{2}[./-]\d{2}[./-]\d{4}$/, 'A data de nascimento informada está inválida'),
      cellPhone: Yup.string().required('Você precisa informar seu telefone'),

      address: Yup.string().required('Você precisa informar seu endereço'),
      addressNumber: Yup.string().required('Você precisa informar o número do seu endereço'),
      district: Yup.string().required('Você precisa informar seu bairro'),
      city: Yup.string().required('Você precisa informar sua cidade'),
      state: Yup.string().required('Você precisa informar seu estado'),
      zipcode: Yup.string().required('Você precisa informar seu cep'),
    }),
    onSubmit: (values, actions) => {
      if (optin) {
        const { firstName, lastName } = splitName(values.name);

        const { name, taxDocument, email, birthDate, cellPhone, address, addressNumber, complement, district, city, state, zipcode } = values;

        const birthDateSanitized = birthDate.split('/');
        const cellPhoneSanitized = cellPhoneMask(cellPhone).split(') ');

        request({
          url: `/api/v1/Customer/CustomerLead/${tenantUuid}`,
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            name,
            contactEmail: email,
            taxId: taxDocument,
            birthDate: `${birthDateSanitized[2]}-${birthDateSanitized[1]}-${birthDateSanitized[0]}`,
            contactPhone: `${cellPhoneSanitized[0] && cellPhoneSanitized[0].replace(/\D/g, '')}${
              cellPhoneSanitized[1] && cellPhoneSanitized[1].replace(/\D/g, '')
            }`,
            country: 'BR',
            zipCode: zipcode && zipcode.replace(/\D/g, ''),
            addressLine1: address,
            addressNumber: addressNumber,
            addressLine2: complement,
            neighborhood: district,
            city,
            state,
            affiliateId: Number(affiliateId) || null,
            schoolUuid,
            productUuid,
          },
        })
          .then((response: AxiosResponse<{ apiResultCode: number; apiResultData: string }>) => {
            const { apiResultData } = response.data;

            const storeUrl = 'https://raonyrossetti.carrinho.app';
            const storeId = '1004604';

            window.location.href = `${storeUrl}/one-checkout/ocmtb/${storeId}?firstname=${firstName}&lastname=${lastName}&email=${email}&telephone=${
              cellPhoneSanitized[0] && cellPhoneSanitized[0].replace(/\D/g, '')
            }${cellPhoneSanitized[1] && cellPhoneSanitized[1].replace(/\D/g, '')}&postcode=${
              zipcode && zipcode.replace(/\D/g, '')
            }&address_street_number=${address}&utm_source=${apiResultData}`;
          })
          .catch((error: AxiosError) => {
            const storeUrl = 'https://raonyrossetti.carrinho.app';
            const storeId = '1004604';

            window.location.href = `${storeUrl}/one-checkout/ocmtb/${storeId}?firstname=${firstName}&lastname=${lastName}&email=${email}&telephone=${
              cellPhoneSanitized[0] && cellPhoneSanitized[0].replace(/\D/g, '')
            }${cellPhoneSanitized[1] && cellPhoneSanitized[1].replace(/\D/g, '')}&postcode=${
              zipcode && zipcode.replace(/\D/g, '')
            }&address_street_number=${address}`;

            // setErrorMessage(
            //   `
            //       Ocorreu um erro ao acessar a plataforma de pagamentos.<br />
            //       Por favor, tente novamente em alguns instantes.
            //     `
            // );
          });
      } else {
        setErrorMessage(
          `
          Você precisa ler e aceitar o termo.
        `
        );
      }
    },
  });

  const errorCount = Object.keys(formik.errors).length;

  useEffect(() => {
    if (formik.isSubmitting) setIsSubmited(true);
  }, [formik.isSubmitting]);

  useEffect(() => {
    if (formik.values.zipcode && formik.values.zipcode.length === 9) {
      setLoading(true);

      request({
        url: `https://cep.awesomeapi.com.br/json/${formik.values.zipcode}`,
        method: 'get',
      })
        .then(
          (
            response: AxiosResponse<{
              cep: string;
              address_type: string;
              address_name: string;
              address: string;
              state: string;
              district: string;
              lat: string;
              lng: string;
              city: string;
              city_ibge: string;
              ddd: string;
            }>
          ) => {
            setLoading(false);

            const { address, state, district, city } = response.data;

            formik.setFieldValue('address', `${address}`);
            formik.setFieldValue('state', state);
            formik.setFieldValue('city', city);
            formik.setFieldValue('district', district);
          }
        )
        .catch(() => {
          setLoading(false);
        });
    }
  }, [formik.values.zipcode]);

  const TermButton = styled('span')`
    text-decoration: underline;
    cursor: pointer;
    user-select: none;
  `;

  return (
    <>
      {loading && <Loading alpha />}

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <label htmlFor="name">
            <FormLabel>Nome completo</FormLabel>

            <FormInput
              id="name"
              name="name"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              data-field="name"
              data-error={formik.touched.name && formik.errors.name}
            />
          </label>

          {formik.touched.name && formik.errors.name ? <FormErrorMessage>{formik.errors.name}</FormErrorMessage> : null}
        </Flex>

        <Flex mt={15} flexDirection="column">
          <label htmlFor="email">
            <FormLabel>E-mail</FormLabel>

            <FormInput
              id="email"
              name="email"
              type="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              data-field="email"
              data-error={formik.touched.email && formik.errors.email}
            />
          </label>

          {formik.touched.email && formik.errors.email ? <FormErrorMessage>{formik.errors.email}</FormErrorMessage> : null}
        </Flex>

        <Flex mt={15} flexDirection="column">
          <label htmlFor="taxDocument">
            <FormLabel>CPF</FormLabel>

            <FormInput
              id="taxDocument"
              name="taxDocument"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={cpfMask(formik.values.taxDocument)}
              data-field="taxDocument"
              data-error={formik.touched.taxDocument && formik.errors.taxDocument}
            />
          </label>

          {formik.touched.taxDocument && formik.errors.taxDocument ? <FormErrorMessage>{formik.errors.taxDocument}</FormErrorMessage> : null}
        </Flex>

        <Flex mt={15} flexDirection="column">
          <label htmlFor="birthDate">
            <FormLabel>Data de nascimento</FormLabel>

            <FormInput
              id="birthDate"
              name="birthDate"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={dateMask(formik.values.birthDate)}
              maxLength={10}
              data-field="birthDate"
              data-error={formik.touched.birthDate && formik.errors.birthDate}
              placeholder="DD/MM/AAAA"
            />
          </label>

          {formik.touched.birthDate && formik.errors.birthDate ? <FormErrorMessage>{formik.errors.birthDate}</FormErrorMessage> : null}
        </Flex>

        <Flex mt={15} flexDirection="column">
          <label htmlFor="cellPhone">
            <FormLabel>DDD + Telefone</FormLabel>

            <FormInput
              id="cellPhone"
              name="cellPhone"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={cellPhoneMask(formik.values.cellPhone)}
              maxLength={15}
              data-field="cellPhone"
              data-error={formik.touched.cellPhone && formik.errors.cellPhone}
            />
          </label>

          {formik.touched.cellPhone && formik.errors.cellPhone ? <FormErrorMessage>{formik.errors.cellPhone}</FormErrorMessage> : null}
        </Flex>

        <Hr />
        <Flex mt={15}>
          <Flex maxWidth={200} mt={{ m: 15, d: 0 }} flexDirection="column">
            <label htmlFor="cellPhone">
              <FormLabel>CEP</FormLabel>

              <FormInput
                id="zipcode"
                name="zipcode"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={cepMask(formik.values.zipcode)}
                maxLength={9}
                data-field="zipcode"
                data-error={formik.touched.zipcode && formik.errors.zipcode}
              />
            </label>

            {formik.touched.zipcode && formik.errors.zipcode ? <FormErrorMessage>{formik.errors.zipcode}</FormErrorMessage> : null}
          </Flex>
        </Flex>

        <Flex mt={15} flexWrap="nowrap">
          <Flex flexGrow={1} flexDirection="column">
            <label htmlFor="address">
              <FormLabel>Endereço</FormLabel>

              <FormInput
                id="address"
                name="address"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address}
                data-field="address"
                data-error={formik.touched.address && formik.errors.address}
              />
            </label>

            {formik.touched.address && formik.errors.address ? <FormErrorMessage>{formik.errors.address}</FormErrorMessage> : null}

            {formik.touched.addressNumber && formik.errors.addressNumber ? <FormErrorMessage>{formik.errors.addressNumber}</FormErrorMessage> : null}
          </Flex>

          <Flex ml="20px" flexDirection="column">
            <label htmlFor="cellPhone">
              <FormLabel>Número</FormLabel>

              <FormInput
                id="addressNumber"
                name="addressNumber"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.addressNumber}
                data-field="addressNumber"
                data-error={formik.touched.addressNumber && formik.errors.addressNumber}
              />
            </label>
          </Flex>
        </Flex>

        <Flex mt={15} flexGrow={1} flexDirection="column">
          <label htmlFor="address">
            <FormLabel>Complemento</FormLabel>

            <FormInput
              id="complement"
              name="complement"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.complement}
              data-field="complement"
              data-error={formik.touched.complement && formik.errors.complement}
            />
          </label>

          {formik.touched.complement && formik.errors.complement ? <FormErrorMessage>{formik.errors.complement}</FormErrorMessage> : null}
        </Flex>

        <Flex mt={15} flexDirection={{ m: 'column', d: 'row' }} flexWrap={{ m: 'wrap', d: 'nowrap' }}>
          <Flex flexGrow={1} flexDirection="column">
            <label htmlFor="address">
              <FormLabel>Bairro</FormLabel>

              <FormInput
                id="district"
                name="district"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.district}
                data-field="district"
                data-error={formik.touched.district && formik.errors.district}
              />
            </label>

            {formik.touched.district && formik.errors.district ? <FormErrorMessage>{formik.errors.district}</FormErrorMessage> : null}
          </Flex>

          <Flex flexGrow={1} ml={{ m: 0, d: 20 }} mt={{ m: 15, d: 0 }} flexDirection="column">
            <label htmlFor="cellPhone">
              <FormLabel>Cidade</FormLabel>

              <FormInput
                id="city"
                name="city"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.city}
                data-field="city"
                data-error={formik.touched.city && formik.errors.city}
              />
            </label>

            {formik.touched.city && formik.errors.city ? <FormErrorMessage>{formik.errors.city}</FormErrorMessage> : null}
          </Flex>

          <Flex maxWidth={{ m: 'auto', d: 70 }} flexGrow={1} ml={{ m: 0, d: 20 }} mt={{ m: 15, d: 0 }} flexDirection="column">
            <label htmlFor="address">
              <FormLabel>UF</FormLabel>

              <FormInput
                id="state"
                name="state"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.state}
                maxLength={2}
                data-field="state"
                data-error={formik.touched.state && formik.errors.state}
              />
            </label>

            {formik.touched.state && formik.errors.state ? <FormErrorMessage>{formik.errors.state}</FormErrorMessage> : null}
          </Flex>
        </Flex>

        <Box mt="30px">
          <Flex mb={20} flexWrap="nowrap" bg={isSubmited && !optin ? 'alert_400' : 'transparent'} py={isSubmited && !optin ? '10px' : '0'}>
            <Box mr={2}>
              <input
                type="checkbox"
                onChange={(e) => {
                  setOptin(e.target.checked);
                }}
                checked={optin}
                name="optin"
                id="optin"
              />
            </Box>

            <Box flexGrow={1} pt="2px">
              <label htmlFor="optin">
                <Typography type="type2" color="white">
                  Declaro que li e aceito o{' '}
                  <TermButton onClick={() => setShowTerm(!showTerm)}>Termo de Adesão para Mentoria Na Rota do Mercado Financeiro</TermButton>
                </Typography>
              </label>
            </Box>
          </Flex>

          {Boolean(isSubmited && (errorCount || errorMessage)) && (
            <Box my="20px">
              <Typography type="type1" color="alert_400">
                {errorMessage ? (
                  <div dangerouslySetInnerHTML={{ __html: errorMessage }} />
                ) : (
                  <>
                    {errorCount === 1 ? 'Ocorreu um erro ao enviar seus dados.' : 'Ocorreram alguns erros ao enviar seus dados.'}
                    <br />
                    Por favor, verifique os campos em vermelho acima e tente novamente.
                  </>
                )}
              </Typography>
            </Box>
          )}

          <FormSubmitButton data-disabled={Boolean(errorCount || errorMessage)} type="submit">
            <Typography type="type2" color="white" fontWeight="bold" upper>
              Continuar para forma de pagamento
            </Typography>
          </FormSubmitButton>
        </Box>

        <Box mt="10px">
          <Typography type="type0" color="gray_500">
            Você será redirecionado para o site parceiro de pagamentos.
          </Typography>
        </Box>
      </form>
    </>
  );
};

export default SignupForm;
